import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  AppBar,
  Container,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  AutoAwesome as SimilarChannelsIcon,
  Explore as SubnicheFinderIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Build as BuildIcon,
  AutoGraph as AutoGraphIcon,
  Favorite as FavoriteIcon,
  WavingHand as WavingHandIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './AuthContext';
import ChannelTracker from './ChannelTracker';
import FolderDetail from './FolderDetail';
import SearchPage from './SearchPage';
import Channel from './Channel';
import SimilarChannels from './SimilarChannels';
import SubnicheFinder from './SubnicheFinder';
import OrganizationManagement from './OrganizationManagement';
import AnalyticsOverview from './AnalyticsOverview';
import AnalyticsUserDetail from './AnalyticsUserDetail';
import Login from './Login';
import ProtectedRoute from './ProtectedRoute';
import { theme } from './theme';

function Navigation() {
  const { user, logout, isAuthenticated, isAdmin, isSuperAdmin, isOwner } = useAuth();
  const [advancedToolsAnchor, setAdvancedToolsAnchor] = React.useState(null);
  const [showAdvancedTools, setShowAdvancedTools] = React.useState(false);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowAdvancedTools(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowAdvancedTools(false);
        setAdvancedToolsAnchor(null);
      }
    };

    const handleWindowBlur = () => {
      setShowAdvancedTools(false);
      setAdvancedToolsAnchor(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const handleLogout = () => {
    logout();
  };

  const toCamelCase = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleAdvancedToolsOpen = (event) => {
    setAdvancedToolsAnchor(event.currentTarget);
  };

  const handleAdvancedToolsClose = () => {
    setAdvancedToolsAnchor(null);
  };

  return (
    <AppBar
      position="static"
      sx={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
        boxShadow: 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            gap: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              component={Link}
              to={isSuperAdmin ? '/workspaces' : '/'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                mr: 2,
                cursor: 'pointer',
              }}
            >
              <AutoGraphIcon
                sx={{
                  fontSize: 28,
                  color: '#818cf8',
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  background: 'linear-gradient(135deg, #818cf8 0%, #c084fc 50%, #e879f9 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  whiteSpace: 'nowrap',
                }}
              >
                Outlier Tracker
              </Typography>
            </Box>

            {isAuthenticated && !isSuperAdmin && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="inherit"
                  component={Link}
                  to="/"
                  startIcon={<HomeIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'rgba(226,232,240,0.95)',
                    border: '1px solid rgba(148,163,184,0.25)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      borderColor: 'rgba(148,163,184,0.35)',
                    },
                  }}
                >
                  Home
                </Button>
                <Button
                  color="inherit"
                  component={Link}
                  to="/search"
                  startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'rgba(226,232,240,0.95)',
                    border: '1px solid rgba(148,163,184,0.25)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      borderColor: 'rgba(148,163,184,0.35)',
                    },
                  }}
                >
                  Search
                </Button>
                {isAdmin && (
                  <Button
                    color="inherit"
                    component={Link}
                    to="/workspaces"
                    startIcon={<PeopleIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'rgba(226,232,240,0.95)',
                      border: '1px solid rgba(148,163,184,0.25)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        borderColor: 'rgba(148,163,184,0.35)',
                      },
                    }}
                  >
                    Workspaces
                  </Button>
                )}
                {isOwner && (
                  <Button
                    color="inherit"
                    component={Link}
                    to="/analytics"
                    startIcon={<AssessmentIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'rgba(226,232,240,0.95)',
                      border: '1px solid rgba(148,163,184,0.25)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        borderColor: 'rgba(148,163,184,0.35)',
                      },
                    }}
                  >
                    Analytics
                  </Button>
                )}
                {showAdvancedTools && (
                  <Button
                    color="inherit"
                    onClick={handleAdvancedToolsOpen}
                    startIcon={<BuildIcon sx={{ fontSize: 18 }} />}
                    endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'rgba(226,232,240,0.95)',
                      border: '1px solid rgba(148,163,184,0.25)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        borderColor: 'rgba(148,163,184,0.35)',
                      },
                    }}
                  >
                    Advanced Tools
                  </Button>
                )}
                <Menu
                  anchorEl={advancedToolsAnchor}
                  open={showAdvancedTools && Boolean(advancedToolsAnchor)}
                  onClose={handleAdvancedToolsClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(148,163,184,0.25)',
                      backdropFilter: 'blur(10px)',
                      '& .MuiMenuItem-root': {
                        color: 'rgba(226,232,240,0.95)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    to="/subniche-finder"
                    onClick={handleAdvancedToolsClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                    }}
                  >
                    <SubnicheFinderIcon sx={{ fontSize: 18 }} />
                    Subniche Finder
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/similar-channels"
                    onClick={handleAdvancedToolsClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                    }}
                  >
                    <SimilarChannelsIcon sx={{ fontSize: 18 }} />
                    Similar Channels
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated && user && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(148,163,184,0.2)',
                  mr: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'rgba(226,232,240,0.95)',
                    letterSpacing: 0.2,
                  }}
                >
                  Welcome back, {toCamelCase(user.username)}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  👋
                </Typography>
              </Box>
            )}
            {isAuthenticated && (isAdmin || isSuperAdmin) && (
              <Tooltip title="Opening my DM to everyone for a week :) Send me a DM letting me know you're Jan's student and I'll follow you!">
                <Button
                  component="a"
                  href="https://x.com/minh_1000"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 2,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'rgba(226,232,240,0.95)',
                    background:
                      'linear-gradient(135deg, rgba(129, 140, 248, 0.15) 0%, rgba(192, 132, 252, 0.15) 100%)',
                    border: '1px solid rgba(129, 140, 248, 0.3)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, rgba(129, 140, 248, 0.25) 0%, rgba(192, 132, 252, 0.25) 100%)',
                      borderColor: 'rgba(129, 140, 248, 0.5)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(129, 140, 248, 0.2)',
                    },
                  }}
                >
                  Say hi to the developer {':)'}
                </Button>
              </Tooltip>
            )}
            {isAuthenticated && (
              <Button
                onClick={handleLogout}
                startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'rgba(226,232,240,0.95)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                  },
                }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

function SuperAdminRedirect({ children }) {
  const { isSuperAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return null;
  }

  // Redirect super admins to /workspaces if they're trying to access any other route
  if (isSuperAdmin && location.pathname !== '/workspaces' && location.pathname !== '/login') {
    return <Navigate to="/workspaces" replace />;
  }

  return children;
}

function CatchAllRoute() {
  const { isSuperAdmin, loading } = useAuth();

  if (loading) {
    return null;
  }

  // Redirect super admins to /workspaces, others to home
  return <Navigate to={isSuperAdmin ? '/workspaces' : '/'} replace />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ flexGrow: 1 }}>
            <Navigation />

            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <ChannelTracker />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/folders/:id"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <FolderDetail />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/channels/:channelId"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <Channel />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/search"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <SearchPage />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/similar-channels"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <SimilarChannels />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/subniche-finder"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth="xl" sx={{ mt: 4 }}>
                      <ProtectedRoute>
                        <SubnicheFinder />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/workspaces"
                element={
                  <Container maxWidth="xl" sx={{ mt: 4 }}>
                    <ProtectedRoute requireAdmin={true}>
                      <OrganizationManagement />
                    </ProtectedRoute>
                  </Container>
                }
              />
              <Route
                path="/analytics"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, sm: 3, md: 4 } }}>
                      <ProtectedRoute requireOwner>
                        <AnalyticsOverview />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route
                path="/analytics/users/:userId"
                element={
                  <SuperAdminRedirect>
                    <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, sm: 3, md: 4 } }}>
                      <ProtectedRoute requireOwner>
                        <AnalyticsUserDetail />
                      </ProtectedRoute>
                    </Container>
                  </SuperAdminRedirect>
                }
              />
              <Route path="*" element={<CatchAllRoute />} />
            </Routes>
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
